import React from "react"
import { Box, Grid, IconButton, Tooltip, Typography, useTheme } from "@material-ui/core"
import {
	Close as CloseIcon
} from "@material-ui/icons"
import { FileWarning as FileWarningIcon, FileCheck as FileCheckIcon, FileX as FileXIcon } from "lucide-react"
import clsx from "clsx"

import MediaService, { Media } from "@/services/Media"
import { getMediaSizeLimitInBytes, formatMediaSizeLimit } from "@/config/mediaLimits"
import { themeColors } from "letalkui"

import AudioPlayer from "@/components/Messages/MediaMessage/AudioPlayer"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/Input/MediaPreview/MediaList/styles"

import { Divider } from "@/components"
import { beautifyBytes, convertBytesToMegabytes } from "@/utils/format"
import { MessageType } from "@/protocols/channel"
import { useGlobalStateStore } from "@/store/GlobalState"

type MediaListProps = {
	medias: Media[]
	onRemove: (mediaIndex: number) => void
}

const FILE_ICON_SIZE_IN_PIXELS = 20

const MediaList: React.FC<MediaListProps> = (props) => {
	const { medias, onRemove } = props

	const theme = useTheme()
	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const channelType = globalStateStore.instance.current_channel_type
	const fileSizeLimitToReceiveWarningInMb = 5
	const getMediaTitle = (media: Media): string => {
		const mediaTypeToMediaTitle: Partial<Record<MessageType, string>> = {
			audio: beautifyBytes(media.data.size, 1),
			video: media.data.name,
			picture: media.data.name,
			file: media.data.name
		}

		const mediaTitle = mediaTypeToMediaTitle[media.type] || ""

		return mediaTitle
	}

	const getMediaFileIcon = (
		isMediaBlocked: boolean,
		isMediaBlockWarning: boolean
	): JSX.Element => {
		if (isMediaBlocked) {
			return (
				<Box className={classes.fileMediaContainer}>
					<FileXIcon
						size={FILE_ICON_SIZE_IN_PIXELS}
						strokeWidth={1.5}
						color={themeColors.colors.error[600]}
					/>
				</Box>
			)
		}

		if (isMediaBlockWarning) {
			return (
				<Box className={classes.fileMediaContainer}>
					<FileWarningIcon
						size={FILE_ICON_SIZE_IN_PIXELS}
						strokeWidth={1.5}
						color={themeColors.colors.warning[600]}
					/>
				</Box>
			)
		}

		return (
			<Box className={classes.fileMediaContainer}>
				<FileCheckIcon
					size={FILE_ICON_SIZE_IN_PIXELS}
					strokeWidth={1.5}
					color={theme.palette.text.primary}
				/>
			</Box>
		)
	}

	const getMediaPreview = (
		media: Media,
		isMediaBlocked: boolean,
		isMediaBlockWarning: boolean
	): JSX.Element => {
		const mediaTypeToMediaPreview: Partial<Record<MessageType, JSX.Element>> = {
			audio: (
				<Box className={classes.audioMediaContainer}>
					<AudioPlayer
						src={media.url || MediaService.getUrlFromBlob(media.data)}
					/>
				</Box>
			),
			video: (
				<video
					width="100%"
					className={classes.videoMediaContainer}
				>
					<source
						src={media.url || MediaService.getUrlFromBlob(media.data)}
						type={media.data.type}
					/>
				</video>
			),
			picture: (
				<img
					src={media.url || MediaService.getUrlFromBlob(media.data)}
					alt={media.data.name}
					className={classes.pictureMedia}
				/>
			),
			file: getMediaFileIcon(isMediaBlocked, isMediaBlockWarning)
		}

		const mediaPreview = mediaTypeToMediaPreview[media.type] || <></>

		return mediaPreview
	}

	const hasBlockedMediaInBatch = medias.some((batchMedia) => batchMedia.data.size > getMediaSizeLimitInBytes(batchMedia.type, channelType))
	const isSingleMedia = medias.length === 1

	return (
		<Grid
			container
			alignItems="center"
			justify="flex-start"
			wrap="wrap"
			className={classes.mediaBlockContainer}
		>
			{medias.map((media, mediaIndex) => {
				const needFileSizeWarning = (convertBytesToMegabytes(media.data.size) > fileSizeLimitToReceiveWarningInMb)
				const needToShowMediaSize = media.type !== "audio"
				const isMediaBlocked = media.data.size > getMediaSizeLimitInBytes(media.type, channelType)
				const shouldShowFileSize = needToShowMediaSize && (needFileSizeWarning || isMediaBlocked)
				const isMediaBlockWarning = !isMediaBlocked && (hasBlockedMediaInBatch || needFileSizeWarning)
				const isMediaBlockOk = !isMediaBlocked && !isMediaBlockWarning

				const mediaBlockClassName = clsx(
					classes.mediaBlock,
					isMediaBlocked && classes.mediaBlockBlocked,
					isMediaBlockWarning && classes.mediaBlockWarning,
					isMediaBlockOk && classes.mediaBlockOk
				)

				return (
					<Box
						className={mediaBlockClassName}
						key={mediaIndex}
					>
						<Grid
							container
							alignItems="center"
							wrap="nowrap"
						>
							<Grid
								item
							>
								{getMediaPreview(media, isMediaBlocked, isMediaBlockWarning)}
							</Grid>

							<Divider orientation="vertical" size={0.5} />

							<Grid
								item
								className={clsx(
									isSingleMedia ? classes.mediaInfoContainerWide : classes.mediaInfoContainer
								)}
							>
								<Grid
									container
								>
									<Typography
										color="textPrimary"
										className={classes.mediaSizeText}
										variant="inherit"
									>
										{shouldShowFileSize && `${beautifyBytes(media.data.size, 1)} • `}
									</Typography>
									<Tooltip
										title={getMediaTitle(media)}
									>
										<Typography
											color="textPrimary"
											className={clsx(
												isSingleMedia ? classes.mediaInfoTextWide : classes.mediaInfoText
											)}
											variant="inherit"
										>
											{getMediaTitle(media)}
										</Typography>
									</Tooltip>
								</Grid>

								{isMediaBlocked && (
									<Grid
										container
										className={classes.mediaErrorText}
										alignItems="center"
									>
										<Typography
											variant="inherit"
										>
											{`Arquivo maior que o limite do WhatsApp (${formatMediaSizeLimit(media.type, channelType)}). Remova para enviar.`}
										</Typography>
									</Grid>
								)}

								{!isMediaBlocked && hasBlockedMediaInBatch && (
									<Grid
										container
										className={classes.mediaWarningText}
										alignItems="center"
									>
										<Typography
											variant="inherit"
										>
											Este arquivo não será enviado enquanto houver mídia bloqueada no lote.
										</Typography>
									</Grid>
								)}

								{!isMediaBlocked && !hasBlockedMediaInBatch && needFileSizeWarning && (
									<Grid
										container
										className={classes.mediaWarningText}
										alignItems="center"
									>
										<Typography
											variant="inherit"
										>
											Mídias maiores de 5MB podem afetar o envio de outras mensagens da sua fila!
										</Typography>
									</Grid>
								)}
							</Grid>

							<Grid
								item
							>
								<IconButton
									onClick={() => onRemove(mediaIndex)}
								>
									<CloseIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Box>
				)
			})}
		</Grid>
	)
}

export default MediaList
